import Sip from './components/Sip.vue';
import Settings from './components/Settings.vue';
import Collaborators from './components/Collaborators.vue';
import SipCallWidget from './components/SipCallWidget.vue';
import sipCallAdapter from './adapters/sipCallAdapter';
import sipConnectionManager from './managers/SipConnectionManager';
import {
  buildSipProfileStatusSections,
  registerSipProfileStatusRefreshBridge,
} from './hooks/profileStatusSections';

export default {
  install(app, { PluginSystem, store }) {
    console.log('[PLUGIN][SIP-CHANNEL] Plugin installing...');

    // Register SIP channel using channelRegistry (channel-extensibility plugin)
    // This ensures the channel appears in ChannelList component
    if (typeof window !== 'undefined' && window.chatwoot?.channelRegistry) {
      const registered = window.chatwoot.channelRegistry.register({
        key: 'sip',
        type: 'Channel::Sip',
        component: Sip,
        icon: 'i-ri-phone-fill',
        title: 'SIP',
        description:
          'SIP voice channel - Configure SIP server with per-agent extensions',
        metadata: {
          core: false, // Plugin channel
          plugin: 'sip-channel',
          version: '1.0.0',
        },
      });

      if (registered) {
        console.log(
          '[PLUGIN][SIP-CHANNEL] Channel registered in channelRegistry'
        );
      } else {
        console.error(
          '[PLUGIN][SIP-CHANNEL] Failed to register in channelRegistry'
        );
      }
    } else {
      console.warn(
        '[PLUGIN][SIP-CHANNEL] channelRegistry not available, falling back to PluginSystem'
      );

      // Fallback to PluginSystem if channelRegistry not available
      if (PluginSystem?.registerChannelType) {
        PluginSystem.registerChannelType('Channel::Sip', 'SIP');
        console.log(
          '[PLUGIN][SIP-CHANNEL] Channel type registered via PluginSystem fallback'
        );
      }
    }

    // Register SIP content overrides using new Plugin System
    if (PluginSystem) {
      // Register channel type for ChannelName component
      PluginSystem.registerChannelType('Channel::Sip', 'SIP');

      // Override inbox-settings tab (order: 1 = first)
      PluginSystem.registerTab('Settings', {
        key: 'inbox-settings',
        name: 'Settings',
        component: Settings,
        mode: 'override',
        overrideKey: 'inbox-settings',
        order: 1,
        condition: context => {
          return context.inbox && context.inbox.channel_type === 'Channel::Sip';
        },
      });

      // Register content override for inbox-settings component
      PluginSystem.registerContentOverride('Settings', {
        tabKey: 'inbox-settings',
        component: Settings,
        channelType: 'Channel::Sip',
        condition: context => {
          return context.inbox && context.inbox.channel_type === 'Channel::Sip';
        },
      });

      // Override collaborators tab (order: 2 = second)
      PluginSystem.registerTab('Settings', {
        key: 'collaborators',
        name: 'Collaborators',
        component: Collaborators,
        mode: 'override',
        overrideKey: 'collaborators',
        order: 2,
        condition: context => {
          return context.inbox && context.inbox.channel_type === 'Channel::Sip';
        },
      });

      // Register content override for collaborators component
      PluginSystem.registerContentOverride('Settings', {
        tabKey: 'collaborators',
        component: Collaborators,
        channelType: 'Channel::Sip',
        condition: context => {
          return context.inbox && context.inbox.channel_type === 'Channel::Sip';
        },
      });

      // Use new visibility API to show only Settings and Collaborators tabs for SIP inboxes
      PluginSystem.showOnlyTabs(
        'Settings',
        ['inbox-settings', 'collaborators'],
        {
          condition: context => {
            return (
              context.inbox && context.inbox.channel_type === 'Channel::Sip'
            );
          },
        }
      );

      console.log(
        '[PLUGIN][SIP-CHANNEL] Content overrides and visibility rules registered'
      );
    } else {
      console.warn(
        '[PLUGIN][SIP-CHANNEL] PluginSystem not available - content replacements not registered'
      );
    }

    // Add SIP components to window.chatwoot.plugins for accessibility
    if (typeof window !== 'undefined') {
      if (!window.chatwoot) window.chatwoot = {};
      if (!window.chatwoot.plugins) window.chatwoot.plugins = {};
      if (!window.chatwoot.plugins['sip-channel'])
        window.chatwoot.plugins['sip-channel'] = {};

      if (
        typeof window.chatwoot.plugins['sip-channel'].stopStoreWatch ===
        'function'
      ) {
        window.chatwoot.plugins['sip-channel'].stopStoreWatch();
      }

      window.chatwoot.plugins['sip-channel'].Sip = Sip;
      window.chatwoot.plugins['sip-channel'].Settings = Settings;
      window.chatwoot.plugins['sip-channel'].Collaborators = Collaborators;
      window.chatwoot.plugins['sip-channel'].sipConnectionManager =
        sipConnectionManager;
    }

    if (store?.watch) {
      const buildSipUserContext = () => {
        const currentUser = store.getters?.getCurrentUser;
        const currentAccountId =
          store.getters?.getCurrentAccountId || currentUser?.account_id;

        if (!currentUser?.id || !currentAccountId) {
          return null;
        }

        return {
          ...currentUser,
          account_id: currentAccountId,
        };
      };

      const stopStoreWatch = store.watch(
        (_, getters) => {
          const currentUser = getters.getCurrentUser;
          const currentAccountId =
            getters.getCurrentAccountId || currentUser?.account_id;

          if (!currentUser?.id || !currentAccountId) {
            return null;
          }

          return `${currentUser.id}:${currentAccountId}`;
        },
        signature => {
          if (!signature) {
            return;
          }

          const currentUser = buildSipUserContext();
          if (!currentUser) {
            return;
          }

          sipConnectionManager.initialize(currentUser).catch(error => {
            console.error(
              '[PLUGIN][SIP-CHANNEL] Failed to initialize SIP manager from store watcher:',
              error
            );
          });
        },
        { immediate: true }
      );

      if (typeof window !== 'undefined') {
        window.chatwoot.plugins['sip-channel'].stopStoreWatch = stopStoreWatch;
      }
    }

    if (PluginSystem && typeof window !== 'undefined') {
      const pluginState = window.chatwoot.plugins['sip-channel'];

      if (!pluginState.profileStatusHookRegistered) {
        PluginSystem.registerHook(
          'sidebar:profile-status-sections',
          buildSipProfileStatusSections
        );
        pluginState.profileStatusHookRegistered = true;
      }

      if (!pluginState.voiceCallHookRegistered) {
        PluginSystem.registerHook('voice:callable-channel-types', types => {
          if (!Array.isArray(types) || types.includes('Channel::Sip')) {
            return types;
          }

          return [...types, 'Channel::Sip'];
        });
        PluginSystem.registerHook('voice:call-adapter', payload => {
          if (
            payload?.adapter ||
            payload?.inbox?.channel_type !== 'Channel::Sip'
          ) {
            return payload;
          }

          return {
            ...payload,
            adapter: sipCallAdapter,
          };
        });
        PluginSystem.registerHook('voice:resolve-contact-target', payload => {
          if (payload?.inbox?.channel_type !== 'Channel::Sip') {
            return payload;
          }

          if (payload?.target) {
            return payload;
          }

          const fallbackTarget = [
            payload?.identifier,
            payload?.contact?.identifier,
            payload?.contact?.additional_attributes?.sip_remote_identity,
            payload?.contact?.additionalAttributes?.sipRemoteIdentity,
          ]
            .map(value => value?.toString().trim())
            .find(Boolean);

          if (!fallbackTarget) {
            return payload;
          }

          return {
            ...payload,
            target: fallbackTarget,
            source: 'identifier',
          };
        });
        PluginSystem.registerComponent('DashboardOverlays', {
          name: 'sip-call-widget',
          component: SipCallWidget,
        });
        pluginState.voiceCallHookRegistered = true;
      }

      if (typeof pluginState.stopProfileStatusRefreshBridge === 'function') {
        pluginState.stopProfileStatusRefreshBridge();
      }

      pluginState.stopProfileStatusRefreshBridge =
        registerSipProfileStatusRefreshBridge();
    }

    // Register components globally
    app.component('Sip', Sip);
    app.component('SipSettings', Settings);
    app.component('SipCollaborators', Collaborators);
    console.log('[PLUGIN][SIP-CHANNEL] SIP channel registered');

    // Note: SIP connection manager will be initialized on-demand when user interacts with SIP features
    // sipConnectionManager.initialize() - called later when user data is available
  },
};
